import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, HandCoins } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


export default function AdminAffiliatePayouts() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payingId, setPayingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.adminAffiliatePayouts();
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load payouts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const markPaid = async (aff, amountOverride) => {
        const amount = amountOverride ?? aff.approved_payout_eur;
        if (amount <= 0) return;
        const note = window.prompt(
            `Payment note (bonifico, PayPal id, ...) — importo: $${amount.toFixed(2)}`,
            "",
        );
        if (note === null) return; // cancelled
        setPayingId(aff.affiliate_user_id);
        try {
            await api.adminMarkPayoutPaid({
                affiliate_user_id: aff.affiliate_user_id,
                amount_eur: amount,
                admin_note: note,
                payment_method: "bank_transfer",
            });
            toast.success("Payout marked as paid");
            load();
        } catch (e) {
            toast.error(e.message || "Mark paid failed");
        } finally {
            setPayingId(null);
        }
    };

    return (
        <SidebarLayout>
            <div className="max-w-5xl mx-auto py-8 px-4">
                <h1 className="text-2xl font-semibold">Affiliate payouts</h1>
                <p className="text-sm text-text-secondary">
                    Min payout ${data?.min_payout_eur?.toFixed(0) || 20}. Approval automatica dopo 30
                    giorni (finestra rimborso Stripe).
                    {data?.auto_approved_this_call > 0 && (
                        <span className="ml-2 text-[var(--color-secondary)]">
                            (+{data.auto_approved_this_call} appena approvate)
                        </span>
                    )}
                </p>

                {loading || !data ? (
                    <p className="mt-6 text-sm text-text-secondary">Loading…</p>
                ) : (
                    <>
                        <div className="mt-6">
                            <h2 className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-2">
                                <Wallet className="h-3 w-3 text-[var(--color-secondary)]" />
                                Da pagare
                            </h2>
                            <div
                                data-testid="payables-table"
                                className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                            >
                                {data.payables.length === 0 ? (
                                    <p className="p-6 text-sm text-text-secondary">
                                        Nessun payout da eseguire — nessun affiliate ha superato il min di $20.
                                    </p>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                                <th className="px-4 py-3">Affiliate</th>
                                                <th className="px-4 py-3">Code</th>
                                                <th className="px-4 py-3">Pending</th>
                                                <th className="px-4 py-3">Approved</th>
                                                <th className="px-4 py-3">Paid out</th>
                                                <th className="px-4 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.payables.map((a) => (
                                                <tr
                                                    key={a.affiliate_user_id}
                                                    data-testid={`payable-row-${a.affiliate_user_id}`}
                                                    className="border-b border-[var(--color-border)] last:border-0"
                                                >
                                                    <td className="px-4 py-3">
                                                        <p>{a.name}</p>
                                                        <p className="text-[11px] text-text-secondary">
                                                            {a.email}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs tabular-nums">
                                                        {a.affiliate_code}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums">
                                                        ${a.pending_payout_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums text-[var(--color-success)]">
                                                        ${a.approved_payout_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums text-text-secondary">
                                                        ${a.paid_out_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            data-testid={`payable-mark-paid-${a.affiliate_user_id}`}
                                                            onClick={() => markPaid(a)}
                                                            disabled={payingId === a.affiliate_user_id}
                                                            className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-[#EDEDED] px-3 py-1 text-[11px] font-medium text-black hover:opacity-90 disabled:opacity-50"
                                                        >
                                                            <HandCoins className="h-3 w-3" />
                                                            Mark as paid
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="mt-10" data-testid="payout-history">
                            <h2 className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Storico payout
                            </h2>
                            <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {data.history.length === 0 ? (
                                    <p className="p-6 text-sm text-text-secondary">
                                        Nessun payout eseguito.
                                    </p>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                                <th className="px-4 py-3">Data</th>
                                                <th className="px-4 py-3">Affiliate</th>
                                                <th className="px-4 py-3">Amount</th>
                                                <th className="px-4 py-3">Method</th>
                                                <th className="px-4 py-3">Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.history.map((h) => (
                                                <tr
                                                    key={h.id}
                                                    className="border-b border-[var(--color-border)] last:border-0"
                                                >
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {new Date(h.paid_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {h.affiliate_user_id.slice(0, 8)}…
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums">
                                                        ${h.amount_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {h.payment_method}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs max-w-xs truncate">
                                                        {h.admin_note || "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </SidebarLayout>
    );
}
